import { useAuth } from "@/components/auth/auth-context";
import { useToast } from "@/components/ui/use-toast";
import { useGetMentorship } from "@/lib/api/mentorships";
import { useEvents } from "@/lib/hooks/use-events";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

interface ChatOverrideEventPayload {
  mentorshipId: string;
  adminName: string;
  adminId: string;
  timestamp: string;
}

export function useChatOverride(mentorshipId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { addEventListener } = useEvents();
  const queryClient = useQueryClient();
  const { data: mentorship } = useGetMentorship(mentorshipId!);

  const [isOverridden, setIsOverridden] = useState(false);
  const [overriddenBy, setOverriddenBy] = useState<string | null>(null);
  const [overriddenByName, setOverriddenByName] = useState<string | null>(null);

  // Initialize state from mentorship data
  useEffect(() => {
    if (mentorship?.metadata?.override) {
      setIsOverridden(mentorship.metadata.override.isOverridden || false);
      setOverriddenBy(mentorship.metadata.override.adminId || null);
      setOverriddenByName(mentorship.metadata.override.adminName || null);
    }
  }, [mentorship]);

  useEffect(() => {
    if (!user) return;

    const handleOverrideEvent = (payload: ChatOverrideEventPayload) => {
      // Only handle events for the current mentorship
      if (payload.mentorshipId !== mentorshipId) return;

      // Update local state
      setIsOverridden(true);
      setOverriddenBy(payload.adminId);

      // Show toast notification
      toast({
        title: "Chat Override Started",
        description: `${payload.adminName} has started monitoring this chat.`,
      });

      // Refetch mentorship data
      queryClient.invalidateQueries({ queryKey: ["mentorship", mentorshipId] });
    };

    const handleEndOverrideEvent = (payload: ChatOverrideEventPayload) => {
      // Only handle events for the current mentorship
      if (payload.mentorshipId !== mentorshipId) return;

      // Update local state
      setIsOverridden(false);
      setOverriddenBy(null);

      // Show toast notification
      toast({
        title: "Chat Override Ended",
        description: `${payload.adminName} has stopped monitoring this chat.`,
      });

      // Refetch mentorship data
      queryClient.invalidateQueries({ queryKey: ["mentorship", mentorshipId] });
    };

    const unsubscribeStart = addEventListener<ChatOverrideEventPayload>(
      "CHAT_OVERRIDE_STARTED",
      handleOverrideEvent,
    );

    const unsubscribeEnd = addEventListener<ChatOverrideEventPayload>(
      "CHAT_OVERRIDE_ENDED",
      handleEndOverrideEvent,
    );

    return () => {
      unsubscribeStart();
      unsubscribeEnd();
    };
  }, [user, mentorshipId, toast, addEventListener, queryClient]);

  return {
    isOverridden,
    overriddenByName,
    overriddenBy,
  };
}
